/*
* Copyright © 2021 Insticator, Inc. All Rights Reserved.
*
* CONFIDENTIALITY NOTICE: THIS SOFTWARE CONTAINS CONFIDENTIAL INFORMATION OF
* INSTICATOR, INC. USE, DISCLOSURE OR REPRODUCTION IS PROHIBITED WITHOUT THE PRIOR
* EXPRESS WRITTEN PERMISSION OF INSTICATOR, INC.
*/

/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import Component from 'inferno-component';
import Layout from 'components/layouts/layout_container';
import InView from 'utils/inview';
import OutOfContentScreen from 'components/screens/outofcontent/outofcontent_container';
import { trackEvent } from 'utils/tracking/tracking';
import { integration, pageurl, device, isLocalStorageExists, parentWindow } from 'utils/environment';
import { language, languages, cookieEnabled, userAgent, os, isMobile } from 'utils/user_data';
import { injectGAScript } from "utils/google_analytics";
import { log } from 'utils/logger';
import { url } from "utils/urls";
import { makeGETRequest } from 'utils/api/makeGETRequest';
import { makePOSTRequest } from "utils/api/makePOSTRequest";
import { getCookieValue } from 'utils/getCookieValue';
import { uuid } from 'utils/uuid'
import OptinScreen from 'components/screens/optin/optin_container'
import { settings } from 'config/config'


// STYLES
// (css-in-js, imported as JS from external file, turned into CSS via library used in utils)
import styles from 'components/app_styles';
import { createStyles, pxToRem } from 'utils/styles';
const sheet = createStyles(styles, { link: false });


// Temporary generate random uuid if there is no one on window (in case user is in gdpr zone).
// TODO: Update once product loader will always expose userUUID to window.
const isInsticatorUserObjectExists = () => {
  return window.parent && window.parent.Insticator && window.parent.Insticator.user
}
let userUUID = (isInsticatorUserObjectExists() && window.parent.Insticator.user.userUUID) ? window.parent.Insticator.user.userUUID : null
let isPubPortalPreview = settings.embed && settings.embed.isPubPortalPreview
let isConsentRequired = isInsticatorUserObjectExists() ? window.parent.Insticator.user.showGDPRMessage : true // Default to true if user object does not exist

if (isConsentRequired === undefined) {
  isConsentRequired = true
}

if (isPubPortalPreview) {
  isConsentRequired = false
}

const optIn = async () => {
  isConsentRequired = false
  if (isInsticatorUserObjectExists() && typeof window.parent.Insticator.user.optUserIn === 'function') {
    window.parent.Insticator.user.optUserIn()
    const waitForUserId = () => new Promise(resolve => {
      const checkForUserIdInterval = setInterval(() => {
        if (isInsticatorUserObjectExists() && window.parent.Insticator.user.userUUID) {
          userUUID = window.parent.Insticator.user.userUUID
          clearInterval(checkForUserIdInterval)
          resolve()
        }
      }, 10)
    })
    await waitForUserId()
  }
}

/* ========== ~~~~~~~~~~ APP : CHILD ~~~~~~~~~~ ========== */
// PRESENTATIONAL COMPONENT
// receives app state & actions as props from parent "_container" component
// this child component handles local state and uses props passed from parent to read/write application state when necessary
class App extends Component {
  constructor(props) {
    super(props);
    // this.handleScroll = this.handleScroll.bind(this);
    this.inview = new InView(this.props.inViewSettings, this.props.setInViewStatus, this.props.inViewStatus);
    // this.timer = new Timer(); // used for tracking time spent
    this.state = { phase: 'loading' };
    this.inlineStyles = {};
    // Multiple getUserPoints calls can cause b2c server to shut down. To ensure that the call is made only once will be using preventPointsCall variable.
    // preventPointsCall is set to true once the call is made.
    this.preventPointsCall = false;
  }

  /* <<<<<<<<<< LIFECYCLE FUNCTIONS >>>>>>>>>> */
  componentWillMount() {
    this.setEmbedStatusAndGAScript();
    this.setIframeSize();

    // Get ip and geo data.
    // If userGeo is not set on embed's load, it will be received by this call.
    this.props.getUserGeoIp();
  }

  componentDidMount() {
    // ::: tracking: embed loaded ::: //
    trackEvent({ type: 'load', name: 'ceuLoad', target: 'pageLoad' })

    // activate first content
    this.props.setAndActivateContent(this.getFirstQueuedContent());

    // initialize in-view check
    this.inview.initializeInViewCheck();

    // change class for intro animation (header/footer)
    setTimeout(() => { this.setState({ phase: 'loaded' }); }, 1500);

    if (isConsentRequired) {
      this.props.setSessionData({ dataFetchingPermitted: true })
      this.props.displayScreen({
        name: 'optIn',
        type: 'partial',
        visible: true,
        content: <OptinScreen
          useLocalStorage={false}
          processUsersConsent={async () => {
            await optIn()
            this.props.setSessionData({ dataFetchingPermitted: true, cookieCheckDone: true, dmpCallsEnabled: true, userInfo: { userId: userUUID } })
            this.props.displayScreen({
              name: 'optIn',
              type: 'partial',
              visible: false,
              content: null
            });
          }}
        />
      })
    } else {
      this.props.setSessionData({ dataFetchingPermitted: true, cookieCheckDone: true, dmpCallsEnabled: true, userInfo: { userId: userUUID } })
    }
  }

  componentWillReceiveProps(nextProps) {
    const currentContent = this.props.queueStatus.currentContent;
    const currentQueuePosition = this.props.queueStatus.queue.queue.indexOf(currentContent);
    if (currentContent && nextProps.queueStatus.currentContent && this.isActiveQueueContent(this.props, currentContent) && !this.isActiveQueueContent(nextProps, currentContent)) {
      const nextQueuePosition = this.nextQueuePosition(currentContent);
      if (nextQueuePosition !== null) {
        // When
        if (nextQueuePosition <= currentQueuePosition) this.handleQueueRestart();
        this.trackQueueEvent(currentQueuePosition, nextQueuePosition);
        this.props.setAndActivateContent(this.props.queueStatus.queue.queue[nextQueuePosition]);
      } else {
        this.trackQueueEvent(currentQueuePosition, nextQueuePosition);
        this.props.completeQueue();
        this.props.displayScreen({
          name: 'outOfContent',
          type: 'full',
          visible: true,
          content: <OutOfContentScreen />
        });
      }
    }
  }

  isActiveQueueContent(passedProps = this.props, currentContent) {
    return passedProps.contentStatus[currentContent]['status'] === 'active';
  }

  nextQueuePosition(currentContent) {
    const currentQueuePosition = this.props.queueStatus.queue.queue.indexOf(currentContent);
    if ((!this.props.queueStatus.repeatQueue || this.props.outOfContent) && currentQueuePosition + 1 >= this.props.queueStatus.queue.queue.length) { return null; }
    const nextPosition = (this.props.queueStatus.queue.queue.indexOf(currentContent) + 1) % this.props.queueStatus.queue.queue.length;
    const nextContent = nextPosition !== null ? this.props.queueStatus.queue.queue[nextPosition] : null;
    const nextContentType = !!nextContent ? this.props.queueStatus.queue.contentTypes[nextContent] : null;
    // Check if the content is allowed or find another one
    return this.isContentAllowed(nextContentType) ? nextPosition : this.nextQueuePosition(nextContent)
  }

  isContentAllowed(nextContentType) {
    return nextContentType !== 'marketResearch' || !this.props.dataFetchingPermitted || (this.props.dataFetchingPermitted && !!this.props.userId)
  }

  trackQueueEvent(currentQueuePosition, nextQueuePosition) {
    if (nextQueuePosition > currentQueuePosition) { return null; }
    if (this.props.outOfContent) trackEvent({ type: 'load', name: 'ceuContentLoad', target: 'outOfContent', additionalProps: { embed: this.props.embedSettings.info } });
  }

  handleQueueRestart() {
    // Reset outOfContent variable back to true.
    // outOfContent is set to _false_ every time the app goes trough the queue if either:
    //      - Any content receives at least one question, or
    //      - Market Research content is in the queue.
    // If the above conditions are not met, outOfContent will remain true which will trigger OOC screen at the end of the queue.
    this.props.setOutOfContent(true);
  }

  getFirstQueuedContent = () => this.props.queueStatus.queue.queue[0];

  setEmbedStatusAndGAScript() {
    let integrationTypeAndURL = {
      integrationType: integration,
      url: pageurl
    };
    let sessionData = { language, languages, cookieEnabled, userAgent, os, pageurl, device, isMobile };
    this.props.setSessionData(sessionData);
    log('sessionStatus', sessionData);
    this.props.setIntegrationTypeAndURL(integrationTypeAndURL);
    injectGAScript(integrationTypeAndURL, this.props.embedSettings);
  }

  setIframeSize() {
    if (window.frameElement) {
      // set line height to zero on insticator-embed DIV to remove any unwanted space
      const embedContainer = window.frameElement.parentElement;
      if (!!embedContainer) embedContainer.style.lineHeight = '0';

      const frameWidth = this.props.embedSettings.styles[device].width;
      // in production we need to explicitly set iframe width and height
      window.frameElement.style.height = this.props.embedSettings.styles[device].height;
      window.frameElement.style.width = frameWidth;
      window.frameElement.border = "none";

      const isFrameWidthSetInUnits = 
        frameWidth.includes('px') ||
        frameWidth.includes('em') ||
        frameWidth.includes('rem')

      this.inlineStyles = {
        'max-width': isFrameWidthSetInUnits ? frameWidth : pxToRem(600)
      }
    } else {
      // set this locally so we can have actual width and height
      this.inlineStyles = {
        width: this.props.embedSettings.styles[device].width,
        height: this.props.embedSettings.styles[device].height
      };
    }
  }

  /* /////////////// RENDER \\\\\\\\\\\\\\\ */
  render() {
    return (
      <div id={`insticator-app`} className={`${sheet.classes.app} ${this.state.phase}`} style={this.inlineStyles}>
        <Layout />
      </div>
    );
  }
}



/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
// will be utilized in parent component after being infused with app state & actions
export default App;
